import { CaretDown, CaretRight, Question } from "@phosphor-icons/react";
import type {
  ClassificationResult,
  ExtractedAnswer,
  ConditionUserOverride,
} from "../types";
import { DdAnswerCard } from "./DdAnswerCard";

interface DdAnswersSectionProps {
  fileId: string;
  classification: ClassificationResult;
  isSaved: boolean;
  isExpanded: boolean;
  expandedConditionDetails: Set<string>;
  expandedExistingAnswers: Set<string>;
  expandedAIDetails: Set<string>;
  onToggleDdExpanded: (fileId: string) => void;
  onToggleConditionDetails: (key: string) => void;
  onToggleExistingAnswer: (key: string) => void;
  onToggleAIDetails: (key: string) => void;
  onToggleSkipped: (fileId: string, questionId: number) => void;
  onUpdateAnswer: (fileId: string, questionId: number, field: keyof ExtractedAnswer, value: any) => void;
  isDdAnswerSkipped: (fileId: string, questionId: number) => boolean;
  getEffectiveDdAnswer: (fileId: string, questionId: number) => ExtractedAnswer | undefined;
  getEffectiveConditionPassed: (fileId: string, questionId: number, conditionId: string, originalPassed: boolean) => boolean;
  getConditionOverride: (fileId: string, questionId: number, conditionId: string) => ConditionUserOverride | undefined;
  onToggleConditionOverride: (fileId: string, questionId: number, conditionId: string, originalPassed: boolean) => void;
  onUpdateOverrideReason: (fileId: string, questionId: number, conditionId: string, reason: string) => void;
}

export function DdAnswersSection({
  fileId,
  classification,
  isSaved,
  isExpanded,
  expandedConditionDetails,
  expandedExistingAnswers,
  expandedAIDetails,
  onToggleDdExpanded,
  onToggleConditionDetails,
  onToggleExistingAnswer,
  onToggleAIDetails,
  onToggleSkipped,
  onUpdateAnswer,
  isDdAnswerSkipped,
  getEffectiveDdAnswer,
  getEffectiveConditionPassed,
  getConditionOverride,
  onToggleConditionOverride,
  onUpdateOverrideReason,
}: DdAnswersSectionProps) {
  const ddAnswers = classification.dueDiligenceAnswers;
  if (!ddAnswers || ddAnswers.extractedAnswers.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-neutral-100">
      <button
        onClick={() => onToggleDdExpanded(fileId)}
        className="flex items-center gap-1.5 text-xs font-medium text-violet-700 hover:text-violet-800 transition-colors"
      >
        {isExpanded ? (
          <CaretDown className="h-3.5 w-3.5" weight="bold" />
        ) : (
          <CaretRight className="h-3.5 w-3.5" weight="bold" />
        )}
        <Question className="h-3.5 w-3.5" weight="duotone" />
        <span>
          {ddAnswers.extractedAnswers.length} Due Diligence{" "}
          {ddAnswers.extractedAnswers.length === 1 ? "vraag" : "vragen"}{" "}
          beantwoord
        </span>
      </button>

      {isExpanded && (
        <div className="mt-2 space-y-2">
          {ddAnswers.extractedAnswers.map((answer) => {
            const effectiveAnswer = getEffectiveDdAnswer(fileId, answer.questionId);
            if (!effectiveAnswer) return null;

            const conditionKey = `${fileId}-${answer.questionId}`;
            const isSkipped = isDdAnswerSkipped(fileId, answer.questionId);

            return (
              <DdAnswerCard
                key={answer.questionId}
                answer={answer}
                effectiveAnswer={effectiveAnswer}
                fileId={fileId}
                isSaved={isSaved}
                isSkipped={isSkipped}
                conditionKey={conditionKey}
                expandedConditionDetails={expandedConditionDetails}
                expandedExistingAnswers={expandedExistingAnswers}
                expandedAIDetails={expandedAIDetails}
                onToggleConditionDetails={onToggleConditionDetails}
                onToggleExistingAnswer={onToggleExistingAnswer}
                onToggleAIDetails={onToggleAIDetails}
                onToggleSkipped={onToggleSkipped}
                onUpdateAnswer={onUpdateAnswer}
                getEffectiveConditionPassed={getEffectiveConditionPassed}
                getConditionOverride={getConditionOverride}
                onToggleConditionOverride={onToggleConditionOverride}
                onUpdateOverrideReason={onUpdateOverrideReason}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
